import { MouseEvent } from 'react';

import { SpecialtyPage } from '@/lib/types';

import Link, { LinkVariant } from '@/components/link';
import Typography from '@/components/typography';

import { ComponentWithTableOfContents } from '@/features/specialty-page';
import { specialtyLinkedSectionID } from '@/features/specialty-page/components/specialty-linked-section';

type SpecialtyTableOfContentsProps = {
  components: SpecialtyPage['specialtyComponents'];
};

type TableOfContentsItem = {
  title: string;
  url: string;
};

function tocItemsFromComponents(components: ComponentWithTableOfContents[]) {
  const items: TableOfContentsItem[] = [];
  for (const component of components) {
    const title = component.tableOfContents?.title;
    if (!component.tableOfContents?.enabled || !title) continue;
    const url = `#${specialtyLinkedSectionID(component)}`;
    items.push({ title, url });
  }
  return items;
}

export default function SpecialtyTableOfContents({
  components,
}: SpecialtyTableOfContentsProps) {
  const links = tocItemsFromComponents(components);

  if (links.length === 0) return null;

  const handleItemClick = (item: TableOfContentsItem) => (e: MouseEvent) => {
    const itemElement = document.querySelector(item.url);
    if (!itemElement) return;

    e.preventDefault();
    itemElement.scrollIntoView({ behavior: 'smooth' });
    window.history.pushState(null, '', item.url);
  };

  return (
    <div className='w-full'>
      <div className='mb-10 py-4'>
        <Typography.Heading5 overline>Table of Contents</Typography.Heading5>

        <div className='mt-6 inline-grid gap-4 gap-x-16 lg:grid-cols-2'>
          {links.map((item, index) => (
            <Link
              variant={LinkVariant.LINK}
              key={index}
              href={item.url}
              onClick={handleItemClick(item)}
            >
              {item.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
